import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { mockArtworks } from '../data/mockArtworks';
import { Eye, EyeOff, Save, LogOut, Plus, Edit, Trash2, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Admin = () => {
  const { t } = useLanguage();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [artworks, setArtworks] = useState(mockArtworks);
  const [emailSettings, setEmailSettings] = useState({
    service: 'sendgrid',
    apiKey: '',
    fromEmail: 'delfieux.alexi@gmail.com',
    toEmail: 'delfieux.alexi@gmail.com'
  });
  const [carouselImages, setCarouselImages] = useState([
    'https://raw.githubusercontent.com/delfieuxalexi-afk/alexi-delfieux-portfolio/main/Mockups/Tableau1.jpg',
    'https://raw.githubusercontent.com/delfieuxalexi-afk/alexi-delfieux-portfolio/main/Mockups/Tableau2.jpg',
    'https://raw.githubusercontent.com/delfieuxalexi-afk/alexi-delfieux-portfolio/main/Mockups/Tableau3.jpg',
    'https://raw.githubusercontent.com/delfieuxalexi-afk/alexi-delfieux-portfolio/main/Mockups/Tableau4.jpg',
    'https://raw.githubusercontent.com/delfieuxalexi-afk/alexi-delfieux-portfolio/main/Mockups/Tableau5.jpg',
    'https://raw.githubusercontent.com/delfieuxalexi-afk/alexi-delfieux-portfolio/main/Mockups/Tableau6.jpg'
  ]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [activeTab, setActiveTab] = useState('artworks');
  const [showAddArtwork, setShowAddArtwork] = useState(false);
  const [showEditArtwork, setShowEditArtwork] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [newArtwork, setNewArtwork] = useState({
    title: '',
    series: 'saintMartin',
    year: new Date().getFullYear(),
    size: '',
    medium: 'Acrylic on canvas',
    colors: '',
    price: 0,
    available: true,
    image: '',
    description: { en: '', fr: '' }
  });

  useEffect(() => {
    // Check if already authenticated
    const auth = localStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }

    // Load saved email settings
    const savedSettings = localStorage.getItem('emailSettings');
    if (savedSettings) {
      setEmailSettings(JSON.parse(savedSettings));
    }

    // Load saved carousel images
    const savedCarousel = localStorage.getItem('carouselImages');
    if (savedCarousel) {
      setCarouselImages(JSON.parse(savedCarousel));
    }

    // Load custom artworks or default artworks
    const customArtworks = localStorage.getItem('customArtworks');
    if (customArtworks) {
      setArtworks(JSON.parse(customArtworks));
    } else {
      // Load artwork availability from localStorage
      const savedArtworks = localStorage.getItem('artworkAvailability');
      if (savedArtworks) {
        const availability = JSON.parse(savedArtworks);
        setArtworks(mockArtworks.map(art => ({
          ...art,
          available: availability[art.id] !== undefined ? availability[art.id] : art.available
        })));
      }
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    // Simple authentication - in production, this should be handled by backend
    if (credentials.username === 'AlexiMozer' && credentials.password === 'MozerFUCK2026!*') {
      setIsAuthenticated(true);
      localStorage.setItem('adminAuth', 'true');
      toast({ title: t.language === 'en' ? 'Login successful!' : 'Connexion réussie !' });
    } else {
      toast({ 
        title: t.language === 'en' ? 'Invalid credentials' : 'Identifiants invalides',
        variant: 'destructive'
      });
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('adminAuth');
    setCredentials({ username: '', password: '' });
  };

  const handleAvailabilityChange = (artworkId) => {
    setArtworks(artworks.map(art => 
      art.id === artworkId ? { ...art, available: !art.available } : art
    ));
  };

  const saveArtworkChanges = () => {
    const availability = {};
    artworks.forEach(art => {
      availability[art.id] = art.available;
    });
    localStorage.setItem('artworkAvailability', JSON.stringify(availability));
    toast({ title: t.admin.saved });
  };

  const saveEmailSettings = () => {
    localStorage.setItem('emailSettings', JSON.stringify(emailSettings));
    toast({ title: t.admin.saved });
  };

  const saveCarouselImages = () => {
    localStorage.setItem('carouselImages', JSON.stringify(carouselImages));
    toast({ title: t.admin.saved });
  };

  const addCarouselImage = () => {
    if (newImageUrl.trim()) {
      setCarouselImages([...carouselImages, newImageUrl.trim()]);
      setNewImageUrl('');
    }
  };

  const removeCarouselImage = (index) => {
    setCarouselImages(carouselImages.filter((_, idx) => idx !== index));
  };

  const moveImageUp = (index) => {
    if (index > 0) {
      const newImages = [...carouselImages];
      [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
      setCarouselImages(newImages);
    }
  };

  const moveImageDown = (index) => {
    if (index < carouselImages.length - 1) {
      const newImages = [...carouselImages];
      [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
      setCarouselImages(newImages);
    }
  };

  const handleAddArtwork = () => {
    const colorsArray = newArtwork.colors.split(',').map(c => c.trim()).filter(c => c);
    const artworkToAdd = {
      ...newArtwork,
      id: artworks.length > 0 ? Math.max(...artworks.map(a => a.id)) + 1 : 1,
      colors: colorsArray
    };
    const updatedArtworks = [...artworks, artworkToAdd];
    setArtworks(updatedArtworks);
    
    // Save to localStorage
    const availability = {};
    updatedArtworks.forEach(art => {
      availability[art.id] = art.available;
    });
    localStorage.setItem('artworkAvailability', JSON.stringify(availability));
    localStorage.setItem('customArtworks', JSON.stringify(updatedArtworks));
    
    toast({ title: t.language === 'en' ? 'Artwork added!' : 'Œuvre ajoutée !' });
    setShowAddArtwork(false);
    setNewArtwork({
      title: '',
      series: 'saintMartin',
      year: new Date().getFullYear(),
      size: '',
      medium: 'Acrylic on canvas',
      colors: '',
      price: 0,
      available: true,
      image: '',
      description: { en: '', fr: '' }
    });
  };

  const handleEditArtwork = () => {
    const colorsArray = typeof editingArtwork.colors === 'string' 
      ? editingArtwork.colors.split(',').map(c => c.trim()).filter(c => c)
      : editingArtwork.colors;
    
    const updatedArtwork = {
      ...editingArtwork,
      colors: colorsArray
    };
    
    const updatedArtworks = artworks.map(art => 
      art.id === updatedArtwork.id ? updatedArtwork : art
    );
    setArtworks(updatedArtworks);
    
    // Save to localStorage
    const availability = {};
    updatedArtworks.forEach(art => {
      availability[art.id] = art.available;
    });
    localStorage.setItem('artworkAvailability', JSON.stringify(availability));
    localStorage.setItem('customArtworks', JSON.stringify(updatedArtworks));
    
    toast({ title: t.language === 'en' ? 'Artwork updated!' : 'Œuvre mise à jour !' });
    setShowEditArtwork(false);
    setEditingArtwork(null);
  };

  const handleDeleteArtwork = (artworkId) => {
    if (window.confirm(t.language === 'en' ? 'Are you sure you want to delete this artwork?' : 'Êtes-vous sûr de vouloir supprimer cette œuvre ?')) {
      const updatedArtworks = artworks.filter(art => art.id !== artworkId);
      setArtworks(updatedArtworks);
      
      // Save to localStorage
      const availability = {};
      updatedArtworks.forEach(art => {
        availability[art.id] = art.available;
      });
      localStorage.setItem('artworkAvailability', JSON.stringify(availability));
      localStorage.setItem('customArtworks', JSON.stringify(updatedArtworks));
      
      toast({ title: t.language === 'en' ? 'Artwork deleted!' : 'Œuvre supprimée !' });
    }
  };

  const openEditModal = (artwork) => {
    setEditingArtwork({
      ...artwork,
      colors: Array.isArray(artwork.colors) ? artwork.colors.join(', ') : artwork.colors
    });
    setShowEditArtwork(true);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-20">
        <div className="bg-white rounded-lg p-8 shadow-lg max-w-md w-full">
          <h1 className="text-3xl font-bold mb-6 text-center">{t.admin.title}</h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                {t.admin.username}
              </label>
              <input
                type="text"
                id="username"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                required
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {t.admin.password}
              </label>
              <input
                type="password"
                id="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors font-medium"
            >
              {t.admin.login}
            </button>
          </form>
          <p className="mt-4 text-sm text-gray-500 text-center">
            {t.language === 'en' ? 'Admin access required' : 'Accès administrateur requis'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-5xl font-bold">{t.admin.title}</h1>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>{t.admin.logout}</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('artworks')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'artworks'
                  ? 'border-b-2 border-black text-black'
                  : 'text-gray-600 hover:text-black'
              }`}
            >
              {t.admin.artworks}
            </button>
            <button
              onClick={() => setActiveTab('carousel')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'carousel'
                  ? 'border-b-2 border-black text-black'
                  : 'text-gray-600 hover:text-black'
              }`}
            >
              {t.language === 'en' ? 'Hero Carousel' : 'Carrousel Hero'}
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`px-6 py-4 font-medium transition-colors ${
                activeTab === 'email'
                  ? 'border-b-2 border-black text-black'
                  : 'text-gray-600 hover:text-black'
              }`}
            >
              {t.admin.emailSettings}
            </button>
          </div>
        </div>

        {/* Artworks Tab */}
        {activeTab === 'artworks' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">{t.admin.artworks}</h2>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowAddArtwork(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>{t.language === 'en' ? 'Add Artwork' : 'Ajouter Œuvre'}</span>
                </button>
                <button
                  onClick={saveArtworkChanges}
                  className="flex items-center space-x-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  <span>{t.admin.save}</span>
                </button>
              </div>
            </div>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {artworks.map((artwork) => (
                <div
                  key={artwork.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <img
                      src={artwork.image}
                      alt={artwork.title}
                      className="w-16 h-16 object-cover rounded"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1536924940846-227afb31e2a5?w=100&h=100&fit=crop';
                      }}
                    />
                    <div>
                      <p className="font-semibold">{artwork.title}</p>
                      <p className="text-sm text-gray-600">{artwork.year} - {artwork.size}</p>
                      <p className="text-sm text-gray-500">{artwork.price} €</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openEditModal(artwork)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      title={t.language === 'en' ? 'Edit' : 'Modifier'}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteArtwork(artwork.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title={t.language === 'en' ? 'Delete' : 'Supprimer'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleAvailabilityChange(artwork.id)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-md font-medium transition-colors ${
                        artwork.available
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                    >
                      {artwork.available ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      <span>{artwork.available ? t.gallery.available : t.gallery.sold}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Carousel Management Tab */}
        {activeTab === 'carousel' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">
                {t.language === 'en' ? 'Hero Carousel Images' : 'Images du Carrousel Hero'}
              </h2>
              <button
                onClick={saveCarouselImages}
                className="flex items-center space-x-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
              >
                <Save className="w-4 h-4" />
                <span>{t.admin.save}</span>
              </button>
            </div>

            {/* Add New Image */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold mb-3">
                {t.language === 'en' ? 'Add New Image' : 'Ajouter une Nouvelle Image'}
              </h3>
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  placeholder={t.language === 'en' ? 'Enter image URL...' : 'Entrer l\'URL de l\'image...'}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && addCarouselImage()}
                />
                <button
                  onClick={addCarouselImage}
                  className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                >
                  {t.language === 'en' ? 'Add' : 'Ajouter'}
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {t.language === 'en' 
                  ? 'Use GitHub raw URLs: https://raw.githubusercontent.com/username/repo/main/path/to/image.jpg'
                  : 'Utilisez les URLs brutes de GitHub : https://raw.githubusercontent.com/username/repo/main/path/to/image.jpg'}
              </p>
            </div>

            {/* Current Images List */}
            <div className="space-y-3">
              <h3 className="font-semibold mb-3">
                {t.language === 'en' ? `Current Images (${carouselImages.length})` : `Images Actuelles (${carouselImages.length})`}
              </h3>
              {carouselImages.map((imageUrl, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <img
                    src={imageUrl}
                    alt={`Carousel ${index + 1}`}
                    className="w-24 h-24 object-cover rounded"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/100x100?text=Error';
                    }}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {t.language === 'en' ? `Image ${index + 1}` : `Image ${index + 1}`}
                    </p>
                    <p className="text-xs text-gray-500 truncate max-w-md">{imageUrl}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => moveImageUp(index)}
                      disabled={index === 0}
                      className="p-2 text-gray-600 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title={t.language === 'en' ? 'Move up' : 'Déplacer vers le haut'}
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveImageDown(index)}
                      disabled={index === carouselImages.length - 1}
                      className="p-2 text-gray-600 hover:text-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title={t.language === 'en' ? 'Move down' : 'Déplacer vers le bas'}
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => removeCarouselImage(index)}
                      className="p-2 text-red-600 hover:text-red-800 transition-colors"
                      title={t.language === 'en' ? 'Remove' : 'Supprimer'}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
              {carouselImages.length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  {t.language === 'en' ? 'No images in carousel. Add some above!' : 'Aucune image dans le carrousel. Ajoutez-en ci-dessus !'}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Email Settings Tab */}
        {activeTab === 'email' && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">{t.admin.emailSettings}</h2>
              <button
                onClick={saveEmailSettings}
                className="flex items-center space-x-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
              >
                <Save className="w-4 h-4" />
                <span>{t.admin.save}</span>
              </button>
            </div>
            <div className="space-y-6 max-w-2xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.admin.emailService}
                </label>
                <select
                  value={emailSettings.service}
                  onChange={(e) => setEmailSettings({ ...emailSettings, service: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                >
                  <option value="sendgrid">SendGrid</option>
                  <option value="emailjs">EmailJS</option>
                  <option value="resend">Resend</option>
                  <option value="smtp">Custom SMTP</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.admin.apiKey}
                </label>
                <input
                  type="password"
                  value={emailSettings.apiKey}
                  onChange={(e) => setEmailSettings({ ...emailSettings, apiKey: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder="Your API key or SMTP password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.admin.fromEmail}
                </label>
                <input
                  type="email"
                  value={emailSettings.fromEmail}
                  onChange={(e) => setEmailSettings({ ...emailSettings, fromEmail: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t.admin.toEmail}
                </label>
                <input
                  type="email"
                  value={emailSettings.toEmail}
                  onChange={(e) => setEmailSettings({ ...emailSettings, toEmail: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  {t.language === 'en'
                    ? 'Configure your email service credentials to enable contact form functionality. Settings are saved locally in your browser.'
                    : 'Configurez vos identifiants de service email pour activer la fonctionnalité du formulaire de contact. Les paramètres sont enregistrés localement dans votre navigateur.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Add Artwork Modal */}
        {showAddArtwork && (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">
                  {t.language === 'en' ? 'Add New Artwork' : 'Ajouter une Nouvelle Œuvre'}
                </h3>
                <button onClick={() => setShowAddArtwork(false)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.language === 'en' ? 'Title' : 'Titre'}
                    </label>
                    <input
                      type="text"
                      value={newArtwork.title}
                      onChange={(e) => setNewArtwork({...newArtwork, title: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.language === 'en' ? 'Series' : 'Série'}
                    </label>
                    <select
                      value={newArtwork.series}
                      onChange={(e) => setNewArtwork({...newArtwork, series: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="saintMartin">Saint Martin & Cityscapes</option>
                      <option value="rise">Rise in Saint Martin</option>
                      <option value="bloc">Bloc & Structures</option>
                      <option value="earlyMozer">Early MOZER Era</option>
                      <option value="origins">Origins & Totems</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.language === 'en' ? 'Year' : 'Année'}
                    </label>
                    <input
                      type="number"
                      value={newArtwork.year}
                      onChange={(e) => setNewArtwork({...newArtwork, year: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.language === 'en' ? 'Size' : 'Dimensions'}
                    </label>
                    <input
                      type="text"
                      value={newArtwork.size}
                      onChange={(e) => setNewArtwork({...newArtwork, size: e.target.value})}
                      placeholder="115x75 cm"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.language === 'en' ? 'Medium' : 'Technique'}
                    </label>
                    <input
                      type="text"
                      value={newArtwork.medium}
                      onChange={(e) => setNewArtwork({...newArtwork, medium: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.language === 'en' ? 'Price (€)' : 'Prix (€)'}
                    </label>
                    <input
                      type="number"
                      value={newArtwork.price}
                      onChange={(e) => setNewArtwork({...newArtwork, price: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.language === 'en' ? 'Colors (comma separated)' : 'Couleurs (séparées par virgule)'}
                  </label>
                  <input
                    type="text"
                    value={newArtwork.colors}
                    onChange={(e) => setNewArtwork({...newArtwork, colors: e.target.value})}
                    placeholder="Orange, Blue, Pink, Yellow"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.language === 'en' ? 'Image URL' : 'URL de l\'image'}
                  </label>
                  <input
                    type="text"
                    value={newArtwork.image}
                    onChange={(e) => setNewArtwork({...newArtwork, image: e.target.value})}
                    placeholder="https://raw.githubusercontent.com/..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.language === 'en' ? 'Description (English)' : 'Description (Anglais)'}
                  </label>
                  <textarea
                    value={newArtwork.description.en}
                    onChange={(e) => setNewArtwork({...newArtwork, description: {...newArtwork.description, en: e.target.value}})}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.language === 'en' ? 'Description (French)' : 'Description (Français)'}
                  </label>
                  <textarea
                    value={newArtwork.description.fr}
                    onChange={(e) => setNewArtwork({...newArtwork, description: {...newArtwork.description, fr: e.target.value}})}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={newArtwork.available}
                    onChange={(e) => setNewArtwork({...newArtwork, available: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    {t.language === 'en' ? 'Available for sale' : 'Disponible à la vente'}
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddArtwork(false)}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  {t.language === 'en' ? 'Cancel' : 'Annuler'}
                </button>
                <button
                  onClick={handleAddArtwork}
                  className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                >
                  {t.language === 'en' ? 'Add Artwork' : 'Ajouter l\'Œuvre'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Artwork Modal */}
        {showEditArtwork && editingArtwork && (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold">
                  {t.language === 'en' ? 'Edit Artwork' : 'Modifier l\'Œuvre'}
                </h3>
                <button onClick={() => setShowEditArtwork(false)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.language === 'en' ? 'Title' : 'Titre'}
                    </label>
                    <input
                      type="text"
                      value={editingArtwork.title}
                      onChange={(e) => setEditingArtwork({...editingArtwork, title: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.language === 'en' ? 'Series' : 'Série'}
                    </label>
                    <select
                      value={editingArtwork.series}
                      onChange={(e) => setEditingArtwork({...editingArtwork, series: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="saintMartin">Saint Martin & Cityscapes</option>
                      <option value="rise">Rise in Saint Martin</option>
                      <option value="bloc">Bloc & Structures</option>
                      <option value="earlyMozer">Early MOZER Era</option>
                      <option value="origins">Origins & Totems</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.language === 'en' ? 'Year' : 'Année'}
                    </label>
                    <input
                      type="number"
                      value={editingArtwork.year}
                      onChange={(e) => setEditingArtwork({...editingArtwork, year: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.language === 'en' ? 'Size' : 'Dimensions'}
                    </label>
                    <input
                      type="text"
                      value={editingArtwork.size}
                      onChange={(e) => setEditingArtwork({...editingArtwork, size: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.language === 'en' ? 'Medium' : 'Technique'}
                    </label>
                    <input
                      type="text"
                      value={editingArtwork.medium}
                      onChange={(e) => setEditingArtwork({...editingArtwork, medium: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t.language === 'en' ? 'Price (€)' : 'Prix (€)'}
                    </label>
                    <input
                      type="number"
                      value={editingArtwork.price}
                      onChange={(e) => setEditingArtwork({...editingArtwork, price: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.language === 'en' ? 'Colors (comma separated)' : 'Couleurs (séparées par virgule)'}
                  </label>
                  <input
                    type="text"
                    value={editingArtwork.colors}
                    onChange={(e) => setEditingArtwork({...editingArtwork, colors: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.language === 'en' ? 'Image URL' : 'URL de l\'image'}
                  </label>
                  <input
                    type="text"
                    value={editingArtwork.image}
                    onChange={(e) => setEditingArtwork({...editingArtwork, image: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.language === 'en' ? 'Description (English)' : 'Description (Anglais)'}
                  </label>
                  <textarea
                    value={editingArtwork.description.en}
                    onChange={(e) => setEditingArtwork({...editingArtwork, description: {...editingArtwork.description, en: e.target.value}})}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.language === 'en' ? 'Description (French)' : 'Description (Français)'}
                  </label>
                  <textarea
                    value={editingArtwork.description.fr}
                    onChange={(e) => setEditingArtwork({...editingArtwork, description: {...editingArtwork.description, fr: e.target.value}})}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editingArtwork.available}
                    onChange={(e) => setEditingArtwork({...editingArtwork, available: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    {t.language === 'en' ? 'Available for sale' : 'Disponible à la vente'}
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEditArtwork(false)}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  {t.language === 'en' ? 'Cancel' : 'Annuler'}
                </button>
                <button
                  onClick={handleEditArtwork}
                  className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                >
                  {t.language === 'en' ? 'Save Changes' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
